import React, { useState, useEffect } from 'react';
import { designerService, Designer } from '../services/designer';
import DesignerModal from '../components/DesignerModal';
import AuthenticatedImage from '../components/AuthenticatedImage';

const Designers: React.FC = () => {
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDesigner, setEditingDesigner] = useState<Designer | null>(null);
  const [showDisabled, setShowDisabled] = useState(false);

  useEffect(() => {
    loadDesigners();
  }, [searchTerm, showDisabled]);

  const loadDesigners = async () => {
    try {
      setLoading(true);
      const response = await designerService.getDesigners({
        search: searchTerm || undefined,
        showDisabled: showDisabled,
      });
      setDesigners(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load designers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadDesigners();
  };

  const handleCreate = () => {
    setEditingDesigner(null);
    setIsModalOpen(true);
  };

  const handleEdit = (designer: Designer) => {
    setEditingDesigner(designer);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingDesigner(null);
  };

  const handleModalSave = async () => {
    await loadDesigners();
    handleModalClose();
  };

  const handleToggleStatus = async (designer: Designer) => {
    try {
      await designerService.updateDesigner(designer.id, {
        isActive: !designer.isActive
      });
      await loadDesigners();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update designer status');
    }
  };


  if (loading && designers.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading designers...</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Designer Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage designer profiles and information.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={handleCreate}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add Designer
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="mt-6">
        <form onSubmit={handleSearch} className="mb-4">
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Search designers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Search
            </button>
          </div>
        </form>

        <div className="mb-4 flex items-center">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">Show disabled designers</span>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {designers.map((designer) => (
            <div
              key={designer.id}
              className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200"
            >
              {/* Banner with Logo Overlay */}
              <div className="aspect-[2/1] relative">
                {/* Banner Background */}
                {designer.bannerUrl ? (
                  <AuthenticatedImage
                    src={designer.bannerUrl}
                    alt={`${designer.name} banner`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gray-200" />
                )}

                {/* Overlay for better logo visibility */}
                <div className="absolute inset-0 bg-black/20" />

                {/* Logo Centered */}
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  {designer.logoUrl ? (
                    <AuthenticatedImage
                      src={designer.logoUrl}
                      alt={`${designer.name} logo`}
                      className="object-contain max-w-[70%] max-h-[70%]"
                    />
                  ) : (
                    <h3 className="text-xl font-medium text-white text-center drop-shadow-lg">
                      {designer.name}
                    </h3>
                  )}
                </div>

                {/* Status Badge */}
                <button
                  onClick={() => handleToggleStatus(designer)}
                  className={`absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                    designer.isActive
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                  }`}
                >
                  {designer.isActive ? 'Enabled' : 'Disabled'}
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="text-base font-medium text-gray-900 truncate">
                  {designer.name}
                </h3>
                {designer.description && (
                  <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                    {designer.description}
                  </p>
                )}
                <button
                  onClick={() => handleEdit(designer)}
                  className="mt-3 w-full inline-flex justify-center items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>

      </div>

      {isModalOpen && (
        <DesignerModal
          designer={editingDesigner}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default Designers;